import {useLayoutEffect, useState, type RefObject} from 'react';

const PREVIEW_MAX_WIDTH = 560;
const PREVIEW_GAP = 14;
const PREVIEW_AIR = 20;
const PREVIEW_MIN_WIDTH = 60;
const SIDE_BY_SIDE_MIN_WIDTH = 200;

export type DocumentPreviewFit = {
  imageWidth: number;
  sideBySide: boolean;
};

type FitArgs = {
  width: number;
  height: number;
  aspectRatio: number;
  count: number;
};

function fitDocumentPreview({
  width,
  height,
  aspectRatio,
  count,
}: FitArgs): DocumentPreviewFit | null {
  const available = height - PREVIEW_AIR;
  if (!width || available < 40) return null;

  if (count < 2) {
    return {
      imageWidth: Math.max(
        PREVIEW_MIN_WIDTH,
        Math.min(width, available * aspectRatio, PREVIEW_MAX_WIDTH),
      ),
      sideBySide: false,
    };
  }

  const stacked = Math.min(
    width,
    ((available - PREVIEW_GAP) / 2) * aspectRatio,
    PREVIEW_MAX_WIDTH,
  );
  const sideBySide = Math.min(
    (width - PREVIEW_GAP) / 2,
    available * aspectRatio,
    PREVIEW_MAX_WIDTH,
  );
  const isSideBySide = sideBySide >= SIDE_BY_SIDE_MIN_WIDTH || sideBySide >= stacked;

  return {
    imageWidth: Math.max(PREVIEW_MIN_WIDTH, isSideBySide ? sideBySide : stacked),
    sideBySide: isSideBySide,
  };
}

function useFitDocumentPreview(
  ref: RefObject<HTMLElement | null>,
  {aspectRatio, count, enabled}: {aspectRatio: number; count: number; enabled: boolean},
) {
  const [fit, setFit] = useState<DocumentPreviewFit | null>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!enabled || !element || typeof ResizeObserver === 'undefined') return;

    const measure = () =>
      setFit(
        fitDocumentPreview({
          width: element.clientWidth,
          height: element.clientHeight,
          aspectRatio,
          count,
        }),
      );

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, aspectRatio, count, enabled]);

  return fit;
}

export {PREVIEW_GAP, fitDocumentPreview, useFitDocumentPreview};
